import { compose, map, path, pathOr } from 'ramda'
import React, { useCallback, useRef } from 'react'

import { useImperativeComponents } from './context/ImperativeComponentsContext'
import { useAutofillFocus } from './context/AutofillFocusContext'
import {
  getAutofillConfig,
  hasAutofillValue,
  hasReplyValue,
  normalizeAutofillDisplayValue,
  normalizeAutofillValueForInput
} from '../_form/formatRepliesFormData'
import DynamicInput from '../../../../components/form/DynamicInput'

const ReplyInput = ({ reply, replies, allReplies, props }) => {
  const { components } = useImperativeComponents()
  const autofillFocus = useAutofillFocus()
  const appliedPlaceholderRef = useRef(false)
  const [appliedAutofillValue, setAppliedAutofillValue] = React.useState(null)

  const question = reply.question || {}
  const data = question.data || {}
  const questionId = question.id
  const questionReference = question.reference

  const hasAutofill = Boolean(getAutofillConfig(question))
  const autofillPlaceholder = reply.autofillPlaceholder
  const autofillEditable = question?.data?.autofill?.editable !== false
  const hasValue = hasReplyValue(reply)
  const isEmpty = !hasValue

  const handleFocus = useCallback(
    e => {
      if (
        hasAutofill &&
        isEmpty &&
        hasAutofillValue(autofillPlaceholder) &&
        !appliedPlaceholderRef.current &&
        autofillFocus?.setAutofillValue
      ) {
        appliedPlaceholderRef.current = true
        const valueToApply = normalizeAutofillValueForInput(autofillPlaceholder, question?.type?.name)

        setAppliedAutofillValue(valueToApply)
        autofillFocus.setAutofillValue(questionId, valueToApply)

        if (props?.onChange) {
          props.onChange(valueToApply)
        }
      }
      props?.onFocus?.(e)
    },
    [hasAutofill, isEmpty, autofillPlaceholder, autofillFocus, questionId, question?.type?.name, props]
  )

  const blockQuestions = React.useMemo(() => {
    if (reply.question.type.name === 'block') {
      return reply.question.blockQuestions
    }
    return []
  }, [reply.question])

  const options = React.useMemo(() => compose(pathOr([], ['question', 'options']))(reply), [reply.question])
  const masks = React.useMemo(() => map(path(['pattern']), pathOr([], ['question', 'masks'])(reply)), [reply.question])

  const autofillPlaceholderDisplay =
    !hasValue && hasAutofill && hasAutofillValue(autofillPlaceholder)
      ? normalizeAutofillDisplayValue(autofillPlaceholder)
      : undefined

  const inputPlaceholder = autofillPlaceholderDisplay ?? props?.placeholder
  const inputReadOnly = hasAutofill && !autofillEditable ? true : props?.readOnly
  const inputOnFocus = hasAutofill && autofillPlaceholderDisplay ? handleFocus : props?.onFocus

  const effectiveValue =
    reply.value !== undefined && reply.value !== null && reply.value !== ''
      ? reply.value
      : appliedAutofillValue ?? reply.value

  return (
    <DynamicInput
      shouldUseImperativeComponent
      valueType={question.type?.name}
      value={effectiveValue}
      allowNull={question.allowNull}
      options={options}
      blockQuestions={blockQuestions}
      masks={masks}
      integrationFieldType={question?.data?.integration?.integrationFieldType}
      integrationId={question?.data?.integration?.integrationId}
      questionId={questionId}
      components={components}
      reply={reply}
      replies={replies}
      allReplies={allReplies}
      questionReference={questionReference}
      {...data}
      {...props}
      placeholder={inputPlaceholder}
      readOnly={inputReadOnly}
      onFocus={inputOnFocus}
    />
  )
}

export default ReplyInput
